import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { clearToken, getToken, setToken, setUnauthorizedHandler } from "@/api/client";
import { login as loginRequest, getMe } from "@/api/auth";
import { isAdminRole, type OrgRole } from "@/api/members";

export interface AdminIdentity {
  id: string;
  orgId: string;
  email: string;
  /** RBAC role — gates portal nav/actions. The server stays authoritative. */
  role: OrgRole;
}

const ROLES: readonly OrgRole[] = ["owner", "admin", "member"];

interface AuthContextValue {
  admin: AdminIdentity | null;
  isAuthenticated: boolean;
  /** True for owner|admin — members are restricted to a read-only landing. */
  isAdminCapable: boolean;
  /** True only when a live session was just ended by a 401 (expired/revoked
   *  token) — NOT on a manual sign-out or a never-authenticated visitor. Lets the
   *  login screen explain *why* the user is back here instead of a blank prompt. */
  sessionExpired: boolean;
  /** Authenticate against POST /auth/login and persist the session. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Establish a session from an admin token obtained out-of-band (social login):
   *  store the token, fetch the authoritative identity (incl. role) via GET /auth/me. */
  establishSession: (token: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ADMIN_KEY = "cogent.admin";

function readStoredAdmin(): AdminIdentity | null {
  try {
    const raw = sessionStorage.getItem(ADMIN_KEY);
    if (!raw) return null;
    // Validate the shape rather than blind-cast (vault Type_Assertion): a malformed
    // / partial stored admin (older build, tamper) must read as logged-out, not
    // become a lie that AppShell then derefs (admin.email / admin.orgId).
    const v = JSON.parse(raw) as Partial<AdminIdentity>;
    if (
      typeof v?.id === "string" &&
      typeof v?.orgId === "string" &&
      typeof v?.email === "string" &&
      typeof v?.role === "string" &&
      ROLES.includes(v.role as OrgRole)
    ) {
      return { id: v.id, orgId: v.orgId, email: v.email, role: v.role as OrgRole };
    }
    return null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Rehydrate from sessionStorage: a session survives a reload within the tab
  // but not a tab close (token lives in sessionStorage too).
  const [admin, setAdmin] = useState<AdminIdentity | null>(() => (getToken() ? readStoredAdmin() : null));
  const [sessionExpired, setSessionExpired] = useState(false);

  const signOut = useCallback(() => {
    clearToken();
    try {
      sessionStorage.removeItem(ADMIN_KEY);
    } catch {
      /* no-op */
    }
    setSessionExpired(false); // a deliberate sign-out is not an expiry
    setAdmin(null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await loginRequest({ email, password });
    setToken(res.token);
    try {
      sessionStorage.setItem(ADMIN_KEY, JSON.stringify(res.admin));
    } catch {
      /* no-op */
    }
    setSessionExpired(false); // fresh credentials clear any prior expiry notice
    setAdmin(res.admin);
  }, []);

  const establishSession = useCallback(async (token: string) => {
    setToken(token);
    try {
      // The token alone lacks the RBAC role → ask the server for the authoritative identity.
      const me = await getMe();
      try {
        sessionStorage.setItem(ADMIN_KEY, JSON.stringify(me));
      } catch {
        /* no-op */
      }
      setSessionExpired(false);
      setAdmin(me);
    } catch (err) {
      // getMe failed → don't strand the token we just set (getToken() would read it
      // truthy while admin stays null). Clear it so the session is cleanly logged-out.
      clearToken();
      try {
        sessionStorage.removeItem(ADMIN_KEY);
      } catch {
        /* no-op */
      }
      throw err;
    }
  }, []);

  // A stale/expired token anywhere → one global sign-out, flagged as an expiry so
  // the login screen can explain it. (React 18 batches both updates.)
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSessionExpired(true);
      setAdmin(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      admin,
      isAuthenticated: admin !== null,
      isAdminCapable: admin !== null && isAdminRole(admin.role),
      sessionExpired,
      signIn,
      establishSession,
      signOut,
    }),
    [admin, sessionExpired, signIn, establishSession, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
